<template>
    <div class="file-card" @click="handleDoubleClick" @contextmenu.prevent="handleContextMenu" :class="{ selected }" @mouseenter="showSelectionCircle = true" @mouseleave="showSelectionCircle = false">
        <div class="selection-circle" v-if="showSelectionCircle || selected" @click.stop="toggleSelection">
            <i class="fas fa-check" v-if="selected"></i>
        </div>
        <div class="copy-link-button" v-if="(showSelectionCircle || selected) && !file.isFolder" @click.stop="handleCopyLink">
            <i class="fas fa-link"></i>
        </div>
        <div class="file-preview" v-if="isImage" @mouseenter="hovered = true" @mouseleave="hovered = false">
            <viewer :images="[previewUrl]" v-if="!selectionMode">
                <img :src="previewUrl" :alt="file.name" @click="show" />
            </viewer>
            <img v-else :src="previewUrl" :alt="file.name" />
            <div class="file-name-tooltip" v-if="hovered">{{ file.name }}</div>
        </div>
        <div class="file-icon-container" v-else>
            <div class="file-icon">
                <i :class="iconClass"></i>
            </div>
            <div class="file-name">{{ file.name }}</div>
        </div>
        <div
            class="context-menu"
            v-if="showContextMenu"
            :style="{ left: contextMenuPosition.x + 'px', top: contextMenuPosition.y + 'px' }"
        >
            <div class="context-menu-title">{{ $t("message.actions") }}</div>
            <div class="context-menu-item" v-if="!file.isFolder" @click="handleCopyLink">
                <i class="fas fa-link text-info"></i> Linki Kopyala
            </div>
            <div class="context-menu-item" v-if="!file.isFolder" @click="handleDownload">
                <i class="fas fa-download text-primary"></i> {{ $t("message.download") }}
            </div>
            <div class="context-menu-item" @click="handleDelete"><i class="fas fa-trash text-danger"></i> {{ $t("message.delete")}}</div>
        </div>
    </div>
</template>

<script>
import Vue from "vue";
import "viewerjs/dist/viewer.css";
import Viewer from "v-viewer";

Vue.use(Viewer);

export default Vue.extend({
    name: "FileCard",
    props: {
        file: {
            type: Object,
            required: true,
        },
        readonly: {
            type: Boolean,
            default: false,
        },
        selectionMode: {
            type: Boolean,
            default: false,
        },
        selected: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            hovered: false,
            showContextMenu: false,
            contextMenuPosition: {
                x: 0,
                y: 0,
            },
            showSelectionCircle: false,
        };
    },
    computed: {
        iconClass() {
            if (this.file.isFolder) {
                return "fas fa-folder";
            }

            const mimeType = this.file.asset?.mime;
            if (mimeType?.includes("pdf")) return "fas fa-file-pdf";
            if (mimeType?.includes("word")) return "fas fa-file-word";
            if (mimeType?.includes("excel")) return "fas fa-file-excel";

            return "fas fa-file";
        },
        isImage() {
            const mimeType = this.file.asset?.mime;
            return mimeType?.includes("image");
        },
        previewUrl() {
            // Resim önizleme URL'sini oluşturun
            // Örneğin: `/api/assets/${this.file.asset._id}/preview`
            return this.file.asset?.cdnUrl;
        },
    },
    methods: {
        show() {
            if (!this.selectionMode) {
                this.$el.querySelector(".file-preview img").click();
            }
        },
        handleContextMenu(event) {
            event.preventDefault();
            this.showContextMenu = true;
            this.contextMenuPosition = {
                x: event.clientX,
                y: event.clientY,
            };
        },
        hideContextMenu() {
            this.showContextMenu = false;
        },
        async handleCopyLink() {
            if (!this.file.isFolder && this.file.asset?.cdnUrl) {
                try {
                    await navigator.clipboard.writeText(this.file.asset.cdnUrl);
                    this.$message.success("Link panoya kopyalandı");
                } catch (error) {
                    console.error("Link kopyalama hatası:", error);
                    this.$message.error("Link kopyalanamadı. Lütfen tekrar deneyin.");
                }
            }
            this.hideContextMenu();
        },
        handleDelete() {
            this.$emit("delete", this.file);
            this.hideContextMenu();
        },
        async handleDownload() {
            if (!this.file.isFolder && this.file.asset?.cdnUrl) {
                try {
                    const response = await fetch(this.file.asset.cdnUrl, {
                        method: "GET",
                        mode: "cors",
                    });

                    if (!response.ok) {
                        throw new Error(`HTTP error! status: ${response.status}`);
                    }

                    const blob = await response.blob();
                    const blobUrl = URL.createObjectURL(blob);

                    const link = document.createElement("a");
                    link.href = blobUrl;
                    link.download = this.file.name;
                    link.style.display = "none";
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);

                    URL.revokeObjectURL(blobUrl);
                } catch (error) {
                    console.error("Fetch download failed, trying alternative methods:", error);

                    try {
                        const link = document.createElement("a");
                        link.href = this.file.asset.cdnUrl;
                        link.download = this.file.name;
                        link.target = "_blank";
                        link.rel = "noopener noreferrer";
                        link.style.display = "none";
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);

                        this.$message.success("Dosya yeni sekmede açıldı, indirme işlemi için sayfada sağ tıklayıp \"Resmi/Dosyayı kaydet\" seçeneğini kullanın");
                    } catch (fallbackError) {
                        console.error("All download methods failed:", fallbackError);

                        if (navigator.clipboard) {
                            try {
                                await navigator.clipboard.writeText(this.file.asset.cdnUrl);
                                this.$message.info("Dosya indirilemedi, URL panoya kopyalandı. Tarayıcıya yapıştırıp manuel olarak indirebilirsiniz.");
                            } catch (clipboardError) {
                                this.$message.error("Dosya indirilemedi. Lütfen daha sonra tekrar deneyin.");
                            }
                        } else {
                            this.$message.error("Dosya indirilemedi. Lütfen daha sonra tekrar deneyin.");
                        }
                    }
                }
            }
            this.hideContextMenu();
        },
        handleDoubleClick() {
            this.$emit("click", this.file);
        },
        toggleSelection() {
            this.$emit('toggleSelection', this.file);
        }
    },
    mounted() {
        document.addEventListener("click", this.hideContextMenu);
    },
    beforeDestroy() {
        document.removeEventListener("click", this.hideContextMenu);
    },
});
</script>

<style scoped lang="scss">
.file-card {
    background: rgba(255, 255, 255, 0.9);
    backdrop-filter: blur(10px);
    padding: 16px;
    cursor: pointer;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    width: 140px;
    height: 140px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
    border-radius: 16px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    position: relative;
    overflow: hidden;
    border: 2px solid transparent;

    &:hover {
        transform: translateY(-4px) scale(1.02);
        box-shadow: 0 8px 40px rgba(102, 126, 234, 0.15);
        border-color: rgba(102, 126, 234, 0.3);
    }

    &.selected {
        border: 2px solid #667eea;
        background: linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%);
        transform: translateY(-2px);
        box-shadow: 0 8px 32px rgba(102, 126, 234, 0.2);
    }

    .file-preview {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 100%;
        height: 80px;
        position: relative;
        border-radius: 12px;
        overflow: hidden;

        img {
            max-width: 100%;
            max-height: 100%;
            object-fit: cover;
            cursor: pointer;
            border-radius: 8px;
            transition: all 0.3s ease;
        }

        &:hover {
            img {
                transform: scale(1.05);
                filter: brightness(0.9);
            }
        }

        .preview-icon {
            position: absolute;
            pointer-events: none;
            z-index: 2;
        }

        .file-name-tooltip {
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background-color: rgba(0, 0, 0, 0.6);
            color: white;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 12px;
            font-weight: 500;
            text-align: center;
            padding: 8px;
            z-index: 5;
            pointer-events: none;
            word-break: break-word;
            transition: opacity 0.2s ease;
        }
    }

    .file-icon-container {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        width: 100%;
        height: 100%;
        gap: 8px;
    }

    .file-icon {
        font-size: 2.5rem;
        text-align: center;
        color: #667eea;
        opacity: 0.8;
        transition: all 0.3s ease;

        .fa-folder {
            color: #f59e0b;
        }

        .fa-file-pdf {
            color: #ef4444;
        }

        .fa-file-word {
            color: #2563eb;
        }

        .fa-file-excel {
            color: #059669;
        }
    }

    .file-name {
        text-align: center;
        font-size: 12px;
        font-weight: 500;
        color: #475569;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        max-width: 100%;
        line-height: 1.3;
        margin-top: 8px;
    }

    .selection-circle {
        position: absolute;
        top: 8px;
        right: 8px;
        width: 24px;
        height: 24px;
        border-radius: 50%;
        background: rgba(255, 255, 255, 0.9);
        backdrop-filter: blur(10px);
        border: 2px solid rgba(148, 163, 184, 0.3);
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 2;
        cursor: pointer;
        transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

        &:hover {
            border-color: #667eea;
            background: rgba(102, 126, 234, 0.1);
            transform: scale(1.1);
        }

        .fa-check {
            color: #667eea;
            font-size: 12px;
            font-weight: bold;
        }
    }

    .copy-link-button {
        position: absolute;
        top: 8px;
        left: 8px;
        width: 24px;
        height: 24px;
        border-radius: 50%;
        background: rgba(255, 255, 255, 0.9);
        backdrop-filter: blur(10px);
        border: 2px solid rgba(148, 163, 184, 0.3);
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 2;
        cursor: pointer;
        transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

        &:hover {
            border-color: #06b6d4;
            background: rgba(6, 182, 212, 0.1);
            transform: scale(1.1);
        }

        .fa-link {
            color: #06b6d4;
            font-size: 12px;
            font-weight: bold;
        }
    }

    &.selected {
        .selection-circle {
            border-color: #667eea;
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);

            .fa-check {
                color: white;
            }
        }
    }

    &:hover {
        .file-icon {
            transform: scale(1.1);
            opacity: 1;
        }
    }

    .selection-indicator {
        // ...
        display: flex;
        // ...
    }

    .file-preview {
        width: 100px;
        height: 100px;
        display: flex;
        align-items: center;
        justify-content: center;

        img {
            max-width: 100%;
            max-height: 100%;
            object-fit: contain;
        }
    }

    // ...
}

.context-menu {
    position: fixed;
    background: rgba(255, 255, 255, 0.95);
    backdrop-filter: blur(15px);
    border-radius: 12px;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
    padding: 8px 0;
    z-index: 999999;
    border: 1px solid rgba(255, 255, 255, 0.3);
    min-width: 180px;

    .context-menu-title {
        font-weight: 600;
        padding: 12px 16px;
        border-bottom: 1px solid rgba(0, 0, 0, 0.08);
        color: #1e293b;
        font-size: 13px;
        text-transform: uppercase;
        letter-spacing: 0.5px;
    }

    .context-menu-item {
        display: flex;
        align-items: center;
        padding: 12px 16px;
        cursor: pointer;
        transition: all 0.2s ease;
        font-weight: 500;
        color: #475569;

        &:hover {
            background: rgba(102, 126, 234, 0.1);
            color: #667eea;
        }

        i {
            margin-right: 12px;
            font-size: 16px;
        }

        .text-primary {
            color: #667eea;
        }

        .text-info {
            color: #06b6d4;
        }

        .text-danger {
            color: #ef4444;
        }
    }
}
</style>
